import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import { Link, Image, ArticleHeading2, Body2, IconChevronRight16 } from '@cian/ui-kit';
import * as React from 'react';

import styles from './SellersBannerCarousel.css';
import { Carousel } from '../../../packages/Carousel/Carousel';
import { SELLERS_BANNER_CAROUSEL_OPTIONS } from '../../constants/sellersBannerCarousel';
import { SellersBannerCarouselSlide } from '../SellersBannerCarouselSlide';

import type { ISpecialPromoSecondaryCarouselSchema } from '../../types/specialPromoSecondaryOffers';
import type SwiperCore from 'swiper';

type TProps = {
  carousel: ISpecialPromoSecondaryCarouselSchema;
  source: string;
  onView: (offers: { id: number; position: number }[]) => void;
  onSlideChange: (offers: { id: number; position: number }[]) => void;
  onAgentClick: () => void;
  onOfferClick: (offerId: number, position: number) => void;
  onAllOffersClick: () => void;
};

export const SellersBannerCarousel: React.FC<TProps> = ({
  carousel,
  source,
  onView,
  onSlideChange,
  onAgentClick,
  onOfferClick,
  onAllOffersClick,
}) => {
  const { title, subtitle, agent, offers, allOffersUrl } = carousel;

  const swiperRef = React.useRef<SwiperCore | null>(null);

  const handleView = React.useCallback(() => {
    const slidesPerView =
      typeof swiperRef.current?.params.slidesPerView === 'number'
        ? swiperRef.current.params.slidesPerView
        : SELLERS_BANNER_CAROUSEL_OPTIONS.slidesPerView;

    const visibleOffers = (offers ?? []).slice(0, slidesPerView).map((offer, i) => ({ id: offer.id, position: i }));

    onView(visibleOffers);
  }, [onView, offers]);

  const handleSlideChange = React.useCallback(
    (swiper: SwiperCore) => {
      const slidesPerView =
        typeof swiper.params.slidesPerView === 'number'
          ? swiper.params.slidesPerView
          : SELLERS_BANNER_CAROUSEL_OPTIONS.slidesPerView;

      const { activeIndex } = swiper;
      const visibleOffers = (offers ?? [])
        .slice(activeIndex, activeIndex + slidesPerView)
        .map((offer, i) => ({ id: offer.id, position: activeIndex + i }));

      onSlideChange(visibleOffers);
    },
    [offers, onSlideChange],
  );

  if (!offers || offers.length === 0) {
    return null;
  }

  return (
    <ActionAfterViewObserver callback={handleView} percentVisible={0.8} triggerOnce>
      <div className={styles['container']}>
        <div className={styles['header']}>
          <div className={styles['header-agent']}>
            <a
              href={agent.cardUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onAgentClick}
              className={styles['header-agent-logo']}
            >
              <Image
                src={agent.logoUrl || ''}
                width={52}
                height={52}
                objectFit="cover"
                borderRadius="8px"
                alt={agent.name}
              />
            </a>
            <div className={styles['header-content']}>
              <ArticleHeading2>{title}</ArticleHeading2>
              {subtitle && <Body2>{subtitle}</Body2>}
            </div>
          </div>
          <div className={styles['header-all-offers']}>
            <Link
              href={allOffersUrl}
              fontSize="M"
              fontWeight="bold"
              target="_blank"
              onClick={onAllOffersClick}
              afterIcon={<IconChevronRight16 color="icon-main-default" />}
            >
              Все предложения
            </Link>
          </div>
        </div>
        <Carousel
          {...SELLERS_BANNER_CAROUSEL_OPTIONS}
          onSlideChange={handleSlideChange}
          onSwiper={swiper => {
            swiperRef.current = swiper;
          }}
        >
          {offers.map((offer, index) => (
            <SellersBannerCarouselSlide
              key={offer.id}
              offer={offer}
              source={source}
              onClick={() => onOfferClick(offer.id, index)}
            />
          ))}
        </Carousel>
      </div>
    </ActionAfterViewObserver>
  );
};
